import { createRoutine } from 'redux-saga-routines';
import { Action } from 'redux-actions';

import {
    genApiSchemaState,
    wrapWithModule,
    RESET_APP_ACTION,
} from 'src/utils/redux';

import { ProductDataState, ProductState, UpdateProductPayload } from './types';

const wrapWithNamespace = wrapWithModule('modules/products');

export const productGetAction = createRoutine(wrapWithNamespace('get'));
export const productUpdateAction = createRoutine(wrapWithNamespace('update'));

const initialState: ProductState = genApiSchemaState({
    error: undefined,
    loading: false,
    data: {
        insurances: [],
        upgrades: [],
        extras: [],
    },
});

const productReducer = (
    state = initialState,
    { type, payload }: Action<ProductDataState> & Action<UpdateProductPayload>,
): ProductState => {
    const handler = HANDLERS[type];
    return handler ? handler(state, payload) : state;
};

const handleGetTrigger = (state: ProductState): ProductState => ({
    ...state,
    loading: true,
});

const handleGetSuccess = (
    state: ProductState,
    payload: ProductDataState,
): ProductState => ({
    error: {},
    loading: false,
    data: payload ? payload : state.data,
});

const handleGetError = (state: ProductState, payload: any): ProductState => ({
    ...initialState,
    error: payload,
});

const handleUpdateTrigger = (
    state: ProductState,
    payload: UpdateProductPayload,
): ProductState => ({
    ...state,
    data: {
        insurances: state?.data?.insurances?.map((i) => ({
            ...i,
            isLoading: i.id.productId === payload?.id?.productId,
        })),
        upgrade: state?.data?.upgrade
            ? {
                  ...state?.data?.upgrade,
                  isLoading:
                      state?.data?.upgrade.id.productId ===
                      payload?.id?.productId,
              }
            : undefined,
        extras: Object.keys(state?.data?.extras)?.reduce((acc, extraKey) => {
            return {
                ...acc,
                [extraKey]: {
                    ...state?.data?.extras[extraKey],
                    products: state?.data?.extras[extraKey].products.map(
                        (p) => ({
                            ...p,
                            isLoading:
                                p.id.productId === payload?.id?.productId,
                            isDisabled:
                                p.id.productId !== payload?.id?.productId,
                        }),
                    ),
                },
            };
        }, {}),
    },
});

const handleUpdateFailure = (
    state: ProductState,
    payload: any,
): ProductState => ({
    ...initialState,
    data: {
        upgrade: state.data?.upgrade
            ? { ...state.data?.upgrade, isLoading: false }
            : undefined,
        insurances: state.data?.insurances?.map((i) => ({
            ...i,
            isLoading: false,
        })),
        extras: Object.keys(state?.data?.extras)?.reduce((acc, extraKey) => {
            return {
                ...acc,
                [extraKey]: {
                    ...state?.data?.extras[extraKey],
                    products: state?.data?.extras[extraKey].products.map(
                        (p) => ({ ...p, isLoading: false, isDisabled: false }),
                    ),
                },
            };
        }, {}),
    },
    error: payload,
});

const HANDLERS = {
    [productGetAction.TRIGGER]: handleGetTrigger,
    [productGetAction.SUCCESS]: handleGetSuccess,
    [productGetAction.FAILURE]: handleGetError,
    [productUpdateAction.TRIGGER]: handleUpdateTrigger,
    [productUpdateAction.SUCCESS]: handleGetSuccess,
    [productUpdateAction.FAILURE]: handleUpdateFailure,
    [RESET_APP_ACTION]: () => initialState,
};

export default productReducer;
